/**
 * Server sync service - syncs account/profile data with server.
 */

import { AUTH_API_URL } from './config';

const API_BASE_URL = AUTH_API_URL;

export interface RegisterRequest {
  email: string;
}

export interface RegisterResponse {
  token: string;
  account: {
    id: string;
    email?: string;
    created_at: string;
    recovery_mode: string;
    security_prefs: Record<string, unknown>;
    enabled_apps: string[];
    server_synced: boolean;
    is_developer?: boolean;
    role?: 'user' | 'developer' | 'admin';
  };
  profiles: Array<{
    id: string;
    account_id: string | null;
    profile_type: string;
    name: string;
    verification_level: number;
    visibility: string;
    metadata: Record<string, unknown>;
    created_at: string;
    updated_at: string;
  }>;
}

export interface LoginRequest {
  email: string;
}

export interface LoginResponse extends RegisterResponse {}

export async function registerAccount(email: string): Promise<RegisterResponse> {
  const response = await fetch(`${API_BASE_URL}/api/accounts/register`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Registration failed' }));
    throw new Error(error.error || 'Registration failed');
  }

  return response.json();
}

export async function loginAccount(email: string): Promise<LoginResponse> {
  const response = await fetch(`${API_BASE_URL}/api/accounts/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Login failed' }));
    throw new Error(error.error || 'Login failed');
  }

  return response.json();
}

export async function getAccount(token: string, signal?: AbortSignal): Promise<RegisterResponse['account']> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 6000);
  try {
    const response = await fetch(`${API_BASE_URL}/api/account`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      signal: signal || controller.signal,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: response.status === 401 ? 'Unauthorized: Failed to fetch account' : 'Failed to fetch account' }));
      throw new Error(error.error || (response.status === 401 ? 'Unauthorized: Failed to fetch account' : 'Failed to fetch account'));
    }

    return response.json();
  } finally {
    clearTimeout(timeoutId);
  }
}

export async function getProfiles(token: string, signal?: AbortSignal): Promise<RegisterResponse['profiles']> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 6000);
  try {
    const response = await fetch(`${API_BASE_URL}/api/profiles`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      signal: signal || controller.signal,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: response.status === 401 ? 'Unauthorized: Failed to fetch profiles' : 'Failed to fetch profiles' }));
      throw new Error(error.error || (response.status === 401 ? 'Unauthorized: Failed to fetch profiles' : 'Failed to fetch profiles'));
    }

    const data = await response.json();
    return data.profiles || [];
  } finally {
    clearTimeout(timeoutId);
  }
}

export async function getProfile(
  token: string,
  id: string,
  signal?: AbortSignal
): Promise<RegisterResponse['profiles'][0]> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 6000);
  try {
    const response = await fetch(`${API_BASE_URL}/api/profiles/${encodeURIComponent(id)}`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      signal: signal || controller.signal,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Failed to fetch profile' }));
      throw new Error(error.error || 'Failed to fetch profile');
    }

    return response.json();
  } finally {
    clearTimeout(timeoutId);
  }
}

export async function batchGetProfiles(
  token: string,
  ids: string[],
  signal?: AbortSignal
): Promise<Array<{
  id: string;
  account_id?: string;
  profile_type: string;
  name: string;
  verification_level: number;
  visibility: string;
  metadata?: Record<string, unknown>;
}>> {
  if (ids.length === 0) return [];
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 6000);
  try {
    const response = await fetch(`${API_BASE_URL}/api/profiles/batch`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ ids }),
      signal: signal || controller.signal,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Failed to batch fetch profiles' }));
      throw new Error(error.error || 'Failed to batch fetch profiles');
    }

    const data = await response.json();
    return data.profiles || [];
  } finally {
    clearTimeout(timeoutId);
  }
}

export async function createProfile(
  token: string,
  data: { name: string; profile_type: string; visibility?: string; metadata?: Record<string, unknown>; storage_url?: string }
): Promise<RegisterResponse['profiles'][0]> {
  const response = await fetch(`${API_BASE_URL}/api/profiles`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Failed to create profile' }));
    throw new Error(error.error || 'Failed to create profile');
  }

  return response.json();
}

export async function updateProfile(
  token: string,
  profileId: string,
  data: { name?: string; visibility?: string; metadata?: Record<string, unknown> }
): Promise<RegisterResponse['profiles'][0]> {
  const response = await fetch(`${API_BASE_URL}/api/profiles/${profileId}`, {
    method: 'PATCH',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Failed to update profile' }));
    throw new Error(error.error || 'Failed to update profile');
  }

  return response.json();
}

export async function deleteProfile(token: string, profileId: string): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/api/profiles/${profileId}`, {
    method: 'DELETE',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Failed to delete profile' }));
    throw new Error(error.error || 'Failed to delete profile');
  }
}

export interface ValidateInvitationResponse {
  valid: boolean;
  invitation?: {
    id: string;
    code: string;
    target_email: string | null;
    home_node_id?: string | null;
    is_household?: boolean;
    expires_at: string | null;
  };
  error?: string;
}

export async function validateInvitation(code: string, email?: string): Promise<ValidateInvitationResponse> {
  const url = new URL(`${API_BASE_URL}/api/invitations/${code}/validate`);
  if (email) {
    url.searchParams.set('email', email);
  }

  const response = await fetch(url.toString(), {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Failed to validate invitation' }));
    return { valid: false, error: error.error || 'Failed to validate invitation' };
  }

  return response.json();
}

export interface RegisterWithInvitationRequest {
  code: string;
  email: string;
  first_name?: string;
  last_name?: string;
  /** Pre-auth email-confirmation token embedded in the invitation link. */
  token?: string;
}

export interface RegisterWithInvitationResponse {
  status: string;
  message: string;
  _dev_token?: string;
  access_token?: string;
  token_type?: string;
  expires_in?: number;
  device_id?: string;
  workspace_ids?: string[];
  account?: {
    id: string;
    email?: string;
    created_at?: string;
    recovery_mode?: string;
    security_prefs?: Record<string, unknown>;
    enabled_apps?: string[];
    pinned_apps?: string[];
    onboarding_complete?: boolean;
    server_synced?: boolean;
    email_verified?: boolean;
  };
  profile?: {
    id: string;
    account_id?: string;
    name: string;
    profile_type: string;
    verification_level?: number;
    visibility?: string;
    created_at?: string;
    updated_at?: string;
  };
}


export async function updateAccount(
  token: string,
  updates: { enabled_apps?: string[]; onboarding_complete?: boolean; recovery_mode?: string; pinned_apps?: string[]; security_prefs?: Record<string, unknown> }
): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/api/account`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(updates),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Failed to update account' }));
    throw new Error(error.error || 'Failed to update account');
  }
}

export async function registerWithInvitation(
  data: RegisterWithInvitationRequest
): Promise<RegisterWithInvitationResponse> {
  const response = await fetch(`${API_BASE_URL}/api/invitations/register`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Registration failed' }));
    throw new Error(error.error || 'Registration failed');
  }

  return response.json();
}

// Household Invitations

export interface HouseholdInvitation {
  id: string;
  code: string;
  target_email?: string | null;
  status: string;
  max_uses: number;
  used_count: number;
  created_at: string;
  expires_at?: string | null;
}

export async function createHouseholdInvitation(
  token: string,
  workspaceId: string,
  targetEmail: string
): Promise<HouseholdInvitation> {
  const response = await fetch(`${API_BASE_URL}/api/workspaces/${workspaceId}/invitations`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify({ target_email: targetEmail }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Failed to create invitation' }));
    throw new Error(error.error || 'Failed to create invitation');
  }

  return response.json();
}

export async function listHouseholdInvitations(
  token: string,
  workspaceId: string
): Promise<HouseholdInvitation[]> {
  const response = await fetch(`${API_BASE_URL}/api/workspaces/${workspaceId}/invitations`, {
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Failed to list invitations' }));
    throw new Error(error.error || 'Failed to list invitations');
  }

  const data = await response.json();
  return data.invitations;
}

// Workspace Members

export interface WorkspaceMember {
  workspace_id: string;
  profile_id: string;
  profile_name: string;
  role: string;
  status: string;
  created_at: string;
  updated_at: string;
}

export async function listWorkspaceMembers(
  token: string,
  workspaceId: string
): Promise<WorkspaceMember[]> {
  const response = await fetch(`${API_BASE_URL}/api/workspaces/${workspaceId}/members`, {
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Failed to list members' }));
    throw new Error(error.error || 'Failed to list members');
  }

  const data = await response.json();
  return data.members;
}

export async function removeMemberFromWorkspace(
  token: string,
  workspaceId: string,
  profileId: string
): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/api/workspaces/${workspaceId}/members/${profileId}`, {
    method: 'DELETE',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Failed to remove member' }));
    throw new Error(error.error || 'Failed to remove member');
  }
}

export async function acceptWorkspaceInvitation(
  token: string,
  workspaceId: string
): Promise<WorkspaceMember> {
  const response = await fetch(`${API_BASE_URL}/api/workspaces/${workspaceId}/accept`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Failed to accept invitation' }));
    throw new Error(error.error || 'Failed to accept invitation');
  }

  const data = await response.json();
  return data.member;
}
